/**
 * @Description
 */
import React, { useRef, useEffect, useMemo } from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent } from 'echarts/components';
// 标签自动布局、全局过渡动画等特性
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { EChartsOption } from 'echarts';
import styles from '@/common/components/Charts/V2BarChart/index.module.less';
import cs from 'classnames';
import { floorKeep } from '@lhb/func';

echarts.use([
  SVGRenderer,
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  LabelLayout,
  UniversalTransition,
]);

export interface V2BarChartProps {
  /**
   * @description 图表标题
   */
  title?: string;
  /**
   * @description 图表主题
   */
  theme?: 'blue' | 'green' | 'purple';
  /**
   * @description 图表宽度，默认不指定，跟随外容器
   */
  width?: number | string;
  /**
   * @description 图表高度
   * @default 270px
   */
  height?: number | string;
  /**
   * @description 柱状图显示方向
   * @default vertical
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * @description x轴数据 更多配置见 https://echarts.apache.org/zh/option.html#xAxis
   */
  xAxisData: string[];
  /**
   * @description y轴数据 更多配置见 https://echarts.apache.org/zh/option.html#series-bar
   */
  seriesData: any[];
  /**
   * @description echarts更多配置 详情见https://echarts.apache.org/zh/option.html#title
   * @type EChartsOption
   */
  config?: EChartsOption;
  /**
   * @description 图表外层包装容器的类名
   */
  wrapperClassName?: string;

  percent?: boolean;
  needLegend?: boolean;
  showLabel?: boolean
}

/**
* @description 便捷文档地址
* @see https://reactmobile.lanhanba.net/charts/v2-bar-chart
*/

const PPTBarChart: React.FC<V2BarChartProps> = (props) => {
  const {
    title,
    width,
    xAxisData,
    height = 270,
    direction = 'vertical',
    seriesData = [],
    config = {},
    wrapperClassName,
    percent = false,
    needLegend = true,
    showLabel = true
  } = props;
  const chartRef = useRef<any>(null);
  let chart: any;

  const seriesBaseConfig = {
    type: 'bar',
    smooth: true,
    showSymbol: false,
    itemStyle: {},
    label: {
      show: showLabel,
      position: direction === 'vertical' ? 'top' : 'right',
      textStyle: {
        color: '#222222',
        fontSize: 16,
        lineHeight: 22
      },
      formatter: (params: any) => {
        if (percent) {
          return floorKeep(params.value, 100, 3) + '%';
        } else {
          return params.value;
        }
      }
    },
  };


  // 横轴配置
  const xAxis = {
    type: 'category',
    data: direction === 'vertical' ? xAxisData : xAxisData.reverse(),
    nameTextStyle: {
      fontSize: 13,
      fontFamily: 'PingFangSC-Regular, PingFang SC',
    },
    axisLine: {
      show: false,
      lineStyle: {
        color: '#999',
      },
    },
    axisTick: {
      show: false,
    },
    axisLabel: {
      color: '#222222',
      lineHeight: 22,
      fontSize: 16,
      fontWeight: 400
    },
    ...(config.xAxis || {}),
  };

  // 竖轴配置
  const yAxis = {
    type: 'value',
    axisLabel: {
      formatter: (value: any) => {
        if (percent) {
          return floorKeep(value, 100, 3) + '%';
        } else {
          return value;
        }
      },
      color: '#999999',
      lineHeight: 22,
      fontSize: 16,
      fontWeight: 400,
    },
    nameTextStyle: {
      fontSize: 16,
      fontFamily: 'PingFangSC, PingFang SC',
      lineHeight: 22
    },
    axisLine: {
      lineStyle: {
        color: '#999',
      },
    },
    splitLine: {
      lineStyle: {
        color: ['#eee'],
        width: 1,
      },
    },
    ...(config.yAxis || {}),
  };

  const options = useMemo(() => {
    return {
      title: {
        show: !!title,
        text: title || '',
        textStyle: {
          color: '#222222',
          fontWeight: 500,
          fontFamily: 'PingFangSC-Medium, PingFang SC',
          fontSize: 16,
          lineHeight: 22,
        },
        top: 6,
        left: 6,
        ...(config.title || {}),
      },
      legend: needLegend ? {
        type: 'plain',
        width: '70%',
        itemWidth: 15,
        itemHeight: 15,
        itemGap: 22,
        itemStyle: {
          borderWidth: 0,
        },
        textStyle: {
          fontSize: 13,
          fontWeight: 400,
          color: '#666666',
          lineHeight: 14,
          rich: {
            a: {
              verticalAlign: 'middle',
            },
          },
          padding: [0, 4, 1, 0],
        },
        icon: 'roundRect',
        top: 24,
        left: 22,
        ...(config.legend || {}),
      } : false,
      // color: themeColor[theme],
      grid: {
        left: 50,
        bottom: 36,
        right: 12,
        ...(config.grid || {})
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        backgroundColor: '#222',
        borderColor: '#222',
        borderWidth: 0,
        textStyle: {
          color: '#fff',
          fontWeight: 'normal',
          fontSize: 12,
          fontFamily: 'PingFangSC-Regular, PingFang SC',
        },
        confine: true,
        alwaysShowContent: false,
        ...(config.tooltip || {}),
      },
      xAxis: direction === 'vertical' ? xAxis : yAxis,
      yAxis: direction === 'vertical' ? yAxis : xAxis,
      series: seriesData.map((item) => {
        return {
          ...seriesBaseConfig,
          data: direction === 'vertical' ? item.data : item.data.reverse(),
          ...item,
          label: {
            ...seriesBaseConfig.label,
            ...item.label || {},
          },
          itemStyle: {
            ...seriesBaseConfig.itemStyle,
            ...item.itemStyle
          }

        };
      }),
    };
  }, [seriesData]);

  useEffect(() => {
    if (chartRef.current) {
      chart = echarts.init(chartRef.current, 'light', {
        renderer: 'svg',
        height,
      });
      chart.setOption(options);
    }
    return () => chart?.dispose();
  }, [options]);

  return (
    <div className={cs(styles.V2BarChartWrap, wrapperClassName)} style={{ width, height }}>
      <div ref={chartRef} className={styles.V2BarChart}></div>
    </div>
  );
};

export default PPTBarChart;
